import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { ArrowSmallRight, VyIcon } from '@vy/ui-design-system';
import { INTEGRATION_PROVIDERS, INVOICE_REVIEW_STATUSES, InvoiceReviewStatus } from '@vy/types';
import {
  first,
  fromNullToUndefined,
  isEmptyArray,
  isMultiple,
  isNotNullOrUndefined,
  isNullOrUndefined,
  isSingleItem,
} from '@vy/utils';
import { useMemo } from 'react';
import { useActivatedIntegration } from '../../../../../hooks/use-activated-integration';
import { useMenu } from '../../../../../hooks/use-menu';
import { ShareInvoiceToSlackDialog } from '../../../DynamicInvoiceViewPage/dynamic-invoice-view-components/dynamic-invoice-header/dynamic-invoice-header-components/ShareInvoiceToSlackDialog';
import {
  ShareInvoiceData,
  ShareInvoicesDialog,
} from '../../../invoices-components/share-invoices-dialog/ShareInvoicesDialog';
import { getRecipients } from './SendReminder/SendReminder';

type ShareInvoiceMenuProps = {
  invoices: InvoiceOverview[];
  disabled?: boolean;
};

export const ShareInvoiceMenu: React.FC<ShareInvoiceMenuProps> = ({ invoices, disabled }) => {
  const {
    isOpen: isSlackDialogOpen,
    handleMenuOpen: handleOpenSlackDialog,
    handleMenuClose: handleCloseSlackDialog,
  } = useMenu();
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const isSlackActivated = useActivatedIntegration(INTEGRATION_PROVIDERS.slack, { suspense: false });

  const isSingleInvoiceOperation = isSingleItem(invoices);

  const isMultipleInvoicesSelected = isMultiple(invoices);
  const singleSelectedInvoice = first(invoices);

  const isSlackShareEnabled = useMemo(() => {
    if (isNullOrUndefined(singleSelectedInvoice) || isMultipleInvoicesSelected) {
      return false;
    }

    const allowedStatusesForSlackShare: InvoiceReviewStatus[] = [
      INVOICE_REVIEW_STATUSES.InReview,
      INVOICE_REVIEW_STATUSES.Draft,
    ];

    return isSlackActivated && allowedStatusesForSlackShare.includes(singleSelectedInvoice?.reviewStatus);
  }, [singleSelectedInvoice, isSlackActivated, isMultipleInvoicesSelected]);
  return (
    <>
      <MenuItem onClick={handleMenuOpen} disabled={disabled}>
        <ListItemIcon>
          <VyIcon
            icon="Share"
            color="black"
            style={{
              transform: 'scale(0.7)',
            }}
          />
        </ListItemIcon>

        <ListItemText>Share via</ListItemText>

        <ArrowSmallRight />
      </MenuItem>

      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
      >
        <ShareInvoicesDialog invoicesData={buildShareInvoicesData(invoices)}>
          <MenuItem
            disabled={areSomeInvoicesMissingEmail(invoices)}
            {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.shareViaEmail)}
          >
            <ListItemText>Email</ListItemText>
          </MenuItem>
        </ShareInvoicesDialog>

        {isSingleInvoiceOperation && (
          <MenuItem onClick={handleOpenSlackDialog} disabled={!isSlackShareEnabled}>
            <ListItemText>Slack</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {isNotNullOrUndefined(singleSelectedInvoice) && !isMultipleInvoicesSelected && (
        <ShareInvoiceToSlackDialog
          open={isSlackDialogOpen}
          onClose={handleCloseSlackDialog}
          invoiceId={singleSelectedInvoice.id}
        />
      )}
    </>
  );
};

function buildShareInvoicesData(invoiceOverviews: InvoiceOverview[]): ShareInvoiceData[] {
  return invoiceOverviews.map<ShareInvoiceData>((invoiceOverview) => ({
    amount: invoiceOverview.revenueBreakdown.total,
    currency: invoiceOverview.currency,
    customerName: invoiceOverview.customer.name,
    invoiceId: invoiceOverview.id,
    invoiceNumber: invoiceOverview.invoiceNumber,
    issueDate: fromNullToUndefined(invoiceOverview.issuedAt),
    contactEmails: getRecipients(invoiceOverview.customer.contacts),
    isStripeSyncMissing: invoiceOverview.isStripeSyncMissing,
  }));
}

function hasMissingEmail(invoice: InvoiceOverview) {
  return isEmptyArray(getRecipients(invoice.customer.contacts));
}

function areSomeInvoicesMissingEmail(invoices: InvoiceOverview[]) {
  return invoices.some(hasMissingEmail);
}
